import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { Badge, IconButton } from '../../tailwindcomponents';
import { Menu, RemoveCircle, Info, NewReleases, Notifications } from '../icons';
import CertificateApi from '../../api/certificates';
import ExtraPaymentsAPI from '../../api/extraPayments';
import { myPayment } from '../../types/extraPayments';
import { NAV_ITEMS } from './navItems';
import SidebarNavItem from './SidebarNavItem';
import SidebarFooter from './SidebarFooter';

type SidebarProps = {
    pathname: string,
    collapsed: boolean,
    animating: boolean,
    onToggle: () => void,
}

const Sidebar: FunctionComponent<SidebarProps> = ({
    pathname, collapsed, animating, onToggle,
}) => {
    const me = useSelector((state: RootState) => state.user.me);
    const isMonitorUser = (me.display_name !== undefined) ? me.display_name === "Maintener" : false;
    const routes = isMonitorUser ? [] : (me.user_clearence_groups !== undefined) ? me.user_clearence_groups : [];
    const isExtraPaymentSupervisor = routes.includes("Extra Payment Supervisor");
    const [certificateStatus, setCertificateStatus] = useState('');
    const [supervisorPayments, setSupervisorPayments] = useState<myPayment[]>([]);

    // Carried over verbatim from Draw, dependency array included. `certificateStatus`
    // is both set by and depended on here, so it refetches once more than needed -
    // deliberately not fixed in the same change as a layout rewrite, so any
    // regression stays bisectable.
    useEffect(() => {
        if (me.user_id !== undefined) {
            CertificateApi.GetCertificateStatus(me.user_id).then(res => {
                if (res.status) {
                    setCertificateStatus(res.data.expired_date_status);
                }
            })
        }

        if (isExtraPaymentSupervisor) {
            ExtraPaymentsAPI.GetSupervisorNotifications().then(res => {
                if (res.status === true) {
                    setSupervisorPayments(res.data);
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certificateStatus, me]);

    const certificateTrail =
        certificateStatus === 'Expiring' ? { node: <Info color="secondary" />, text: 'Certificate is expiring within 1 month' }
            : certificateStatus === 'Expired' ? { node: <RemoveCircle color="error" />, text: 'Your certificate has expired' }
                : certificateStatus === 'Updated' ? { node: <NewReleases className="text-green-600" />, text: 'Your certificate has been updated' }
                    : undefined;

    const paymentsTrail = supervisorPayments.length > 0
        ? { node: <span className="mr-2 mt-2 flex"><Badge badgeContent={supervisorPayments.length} color="error"><Notifications color="secondary" /></Badge></span>, text: 'Waiting approval' }
        : undefined;

    const trailFor = (path: string) =>
        path === '/my-profile' ? certificateTrail
            : path === '/extra-payments' ? paymentsTrail
                : undefined;

    const visible = NAV_ITEMS.filter(item =>
        item.accessGroups.length === 0 || routes.some(r => item.accessGroups.includes(r)));

    return (
        <aside
            className="ds-sidebar"
            data-ds-collapsed={collapsed ? 'true' : undefined}
            data-ds-animating={animating ? 'true' : undefined}
            role="navigation"
            aria-label="Main navigation"
        >
            {/* Always visible, always top-left, in every tier and both states. */}
            <div className="flex h-[var(--ds-header-h)] flex-none items-center px-2">
                <IconButton
                    color="inherit"
                    size="large"
                    className="text-[color:var(--ds-sidebar-fg)]"
                    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                    aria-expanded={!collapsed}
                    onClick={onToggle}
                >
                    <Menu />
                </IconButton>
            </div>

            <nav className="ds-nav scrollbar-thin">
                <ul className="ds-nav-list flex flex-col gap-0.5">
                    {visible.map(item => {
                        const trail = trailFor(item.path);
                        return (
                            <SidebarNavItem
                                key={item.path}
                                item={item}
                                // Segment-boundary match. The old `pathname.includes(route)`
                                // matched anywhere in the string, so a route that is a
                                // prefix of another lit both rows.
                                active={pathname === item.path || pathname.startsWith(`${item.path}/`)}
                                collapsed={collapsed}
                                trail={trail?.node}
                                trailText={trail?.text}
                                showDot={trail !== undefined}
                            />
                        );
                    })}
                </ul>
            </nav>

            <SidebarFooter collapsed={collapsed} />
        </aside>
    );
};

export default Sidebar;
